import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Quiz as QuizIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  NotificationsActive as NotificationsActiveIcon,
  Business as BusinessIcon,
  AccountCircle,
  ExitToApp,
  Settings,
  Lock as LockIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Announcement as AnnouncementIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { BottomNavigation } from './BottomNavigation'

const drawerWidth = 270

interface NavigationItem {
  label: string
  path: string
  icon: React.ReactNode
  roles?: string[]
}

// 階層メニュー用のインターフェース
interface MenuCategory {
  id: string
  label: string
  icon: React.ReactNode
  roles?: string[]
  children: NavigationItem[]
}

// ロール表示名の定義（翻訳キーにフォールバック）
const roleDisplayNames = {
  SUPER_ADMIN: 'roles.SUPER_ADMIN',
  CORPORATE_ADMIN: 'roles.CORPORATE_ADMIN',
  USER: 'roles.USER',
}

// トップレベルナビゲーションアイテム（階層化されないもの）
const getTopLevelNavigationItems = (t: (key: string) => string): NavigationItem[] => [
  {
    label: t('nav.dashboard'),
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    label: t('nav.myEnrollments'),
    path: '/my-enrollments',
    icon: <AssignmentIcon />,
  },
]

// 階層メニューカテゴリ定義
const getMenuCategories = (t: (key: string) => string): MenuCategory[] => [
  {
    id: 'learningPlanning',
    label: t('nav.learningPlanning'),
    icon: <SchoolIcon />,
    roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
    children: [
      {
        label: t('nav.compliance'),
        path: '/compliance',
        icon: <DescriptionIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.tests'),
        path: '/tests',
        icon: <QuizIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.testSchedules'),
        path: '/test-schedules',
        icon: <ScheduleIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.learningPlans'),
        path: '/learning-plans',
        icon: <SchoolIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
    ],
  },
  {
    id: 'systemManagement',
    label: t('nav.systemManagement'),
    icon: <AdminPanelSettingsIcon />,
    roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
    children: [
      {
        label: t('nav.users'),
        path: '/users',
        icon: <PeopleIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.reminders'),
        path: '/reminder-manage',
        icon: <NotificationsActiveIcon />,
        roles: ['CORPORATE_ADMIN'],
      },
      {
        label: t('nav.subscription'),
        path: '/subscription',
        icon: <CreditCardIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.invoices'),
        path: '/invoices',
        icon: <ReceiptIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
    ],
  },
]

// スーパー管理者専用メニューカテゴリ（SUPER_ADMIN専用）
const getSuperAdminMenuCategories = (t: (key: string) => string): MenuCategory[] => [
  {
    id: 'superAdminManagement',
    label: t('nav.superAdminManagement'),
    icon: <SecurityIcon />,
    roles: ['SUPER_ADMIN'],
    children: [
      {
        label: t('nav.tenants'),
        path: '/tenant-management',
        icon: <BusinessIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.emailDelivery'),
        path: '/email-delivery',
        icon: <MarkEmailReadIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.emailTemplates'),
        path: '/email-templates',
        icon: <EmailIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.roleFeatures'),
        path: '/role-features',
        icon: <SecurityIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.billing'),
        path: '/billing',
        icon: <PaymentIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.tenantReminders'),
        path: '/tenant-reminder-manage',
        icon: <NotificationsActiveIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.announcements'),
        path: '/announcements',
        icon: <AnnouncementIcon />,
        roles: ['SUPER_ADMIN'],
      },
    ],
  },
]

export const Layout: React.FC = () => {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // 階層メニューの開閉状態を管理
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isLoading, token, canAccessRoute } = useAuth()

  // カテゴリの開閉を切り替える
  const handleCategoryToggle = (categoryId: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }

  // 現在のパスが子アイテムに含まれているかチェック（カテゴリを自動展開するため）
  useEffect(() => {
    const allCategories = [...getMenuCategories(t), ...getSuperAdminMenuCategories(t)]
    const newOpenCategories: Record<string, boolean> = {}

    allCategories.forEach((category) => {
      const hasActiveChild = category.children.some((child) => location.pathname === child.path)
      if (hasActiveChild) {
        newOpenCategories[category.id] = true
      }
    })

    setOpenCategories((prev) => ({
      ...prev,
      ...newOpenCategories,
    }))
  }, [location.pathname, t])

  // デバッグログ追加
  React.useEffect(() => {
    console.log('Layout - Auth状態:', {
      hasUser: !!user,
      userName: user?.name,
      userRole: user?.role,
      isLoading,
      hasToken: !!token,
    })
  }, [user, isLoading, token])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    // テスト実施中のチェック
    const isTestPage = location.pathname.startsWith('/test/')
    if (isTestPage) {
      const confirmLogout = window.confirm(t('dialogs.testInProgressLogout'))

      if (!confirmLogout) {
        handleMenuClose()
        return
      }
    }

    await logout()
    handleMenuClose()
    navigate('/login')
  }

  const handleNavigation = (path: string, itemLabel?: string) => {
    // テスト実施中のチェック
    const isTestPage = location.pathname.startsWith('/test/')
    if (isTestPage) {
      const confirmLeave = window.confirm(
        t('dialogs.testInProgressNavigate', { page: itemLabel || t('nav.dashboard') })
      )

      if (!confirmLeave) {
        if (isMobile) {
          setMobileOpen(false)
        }
        return
      }
    }

    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  // ロール表示名の取得
  const getUserRoleDisplayName = () => {
    if (!user?.role) return t('roles.USER')
    const roleKey = roleDisplayNames[user.role as keyof typeof roleDisplayNames]
    return roleKey ? t(roleKey) : user.role
  }

  // トップレベルアイテム
  const topLevelItems = getTopLevelNavigationItems(t)

  // 階層メニューカテゴリ（ユーザーのロールとロール機能設定でフィルタリング）
  const menuCategories = getMenuCategories(t)
    .filter((category) => {
      if (!category.roles) return true
      return user && category.roles.includes(user.role)
    })
    .map((category) => ({
      ...category,
      // 子アイテムをロール機能設定でフィルタリング
      children: category.children.filter((child) => {
        // まず静的なロールチェック
        if (child.roles && (!user || !child.roles.includes(user.role))) {
          return false
        }
        // ロール機能設定によるアクセス制御
        return canAccessRoute(child.path)
      }),
    }))
    // 子アイテムがないカテゴリは除外
    .filter((category) => category.children.length > 0)

  // スーパー管理者専用カテゴリ
  const superAdminCategories = getSuperAdminMenuCategories(t)
    .filter((category) => {
      if (!category.roles) return true
      return user && category.roles.includes(user.role)
    })
    .map((category) => ({
      ...category,
      // 子アイテムをロール機能設定でフィルタリング
      children: category.children.filter((child) => {
        // まず静的なロールチェック
        if (child.roles && (!user || !child.roles.includes(user.role))) {
          return false
        }
        // ロール機能設定によるアクセス制御
        return canAccessRoute(child.path)
      }),
    }))
    // 子アイテムがないカテゴリは除外
    .filter((category) => category.children.length > 0)

  const drawer = (
    <Box>
      <Toolbar />
      <List>
        {/* トップレベルアイテム（ダッシュボード、理解度テスト） */}
        {topLevelItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path, item.label)}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* 階層メニューカテゴリ（学習計画、システム管理） */}
        {menuCategories.map((category) => (
          <React.Fragment key={category.id}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleCategoryToggle(category.id)}>
                <ListItemIcon>{category.icon}</ListItemIcon>
                <ListItemText primary={category.label} />
                {openCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openCategories[category.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {category.children.map((child) => (
                  <ListItemButton
                    key={child.path}
                    sx={{ pl: 4 }}
                    onClick={() => handleNavigation(child.path, child.label)}
                    selected={location.pathname === child.path}
                  >
                    <ListItemIcon>{child.icon}</ListItemIcon>
                    <ListItemText primary={child.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}

        {/* スーパー管理者専用カテゴリ */}
        {superAdminCategories.map((category) => (
          <React.Fragment key={category.id}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleCategoryToggle(category.id)}>
                <ListItemIcon>{category.icon}</ListItemIcon>
                <ListItemText primary={category.label} />
                {openCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openCategories[category.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {category.children.map((child) => (
                  <ListItemButton
                    key={child.path}
                    sx={{ pl: 4 }}
                    onClick={() => handleNavigation(child.path, child.label)}
                    selected={location.pathname === child.path}
                  >
                    <ListItemIcon>{child.icon}</ListItemIcon>
                    <ListItemText primary={child.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: 'none' }} // スマートフォンでは非表示（下部ナビを使用）
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ComplianceAuto
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                mr: 2,
                textAlign: 'right',
                minWidth: '150px', // 最小幅を確保（テナント名表示のため拡張）
              }}
            >
              {user?.tenantName && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    lineHeight: 1.1,
                    mb: 0.3,
                    fontWeight: 'medium',
                    color: 'rgba(255, 255, 255, 0.8)', // ネイビー背景に対して薄い白
                  }}
                >
                  {user.tenantName}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  minHeight: '1.2em', // 最小高さ確保でレイアウトシフト防止
                  color: '#FFFFFF', // ネイビー背景に対して白
                }}
              >
                {user?.name || t('loading')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  display: 'inline-block',
                  lineHeight: 1.2,
                  minHeight: '1.2em', // 最小高さ確保
                }}
              >
                {getUserRoleDisplayName()}
              </Typography>
            </Box>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuClick}
              color="inherit"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  const isTestPage = location.pathname.startsWith('/test/')
                  if (isTestPage) {
                    const confirmLeave = window.confirm(
                      t('dialogs.testInProgressNavigate', { page: t('nav.profile') })
                    )

                    if (!confirmLeave) {
                      handleMenuClose()
                      return
                    }
                  }
                  navigate('/profile')
                  handleMenuClose()
                }}
              >
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                {t('nav.profile')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const isTestPage = location.pathname.startsWith('/test/')
                  if (isTestPage) {
                    const confirmLeave = window.confirm(
                      t('dialogs.testInProgressNavigate', { page: t('nav.passwordChange') })
                    )

                    if (!confirmLeave) {
                      handleMenuClose()
                      return
                    }
                  }
                  navigate('/change-password')
                  handleMenuClose()
                }}
              >
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.passwordChange')}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" />
                </ListItemIcon>
                {t('nav.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          pb: { xs: 10, md: 3 }, // モバイルで下部ナビのためのマージン
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          minWidth: 0, // overflow対策
          overflowX: 'hidden', // 横スクロール防止
        }}
      >
        <Outlet />
      </Box>

      {/* スマートフォン向け下部ナビゲーション */}
      <BottomNavigation />
    </Box>
  )
}
